import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { kCitatorGetCitingCasesApiPathV2 } from '@/constants/constants-api-paths'
import { nanoid } from 'nanoid'
import { getAuth } from 'firebase/auth'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import * as Sentry from '@sentry/browser'
import { ApiError } from '@/util/errors/api-error'
import { CitatorCitingCasesResponse, CitatorCitingCasesResponseSchema } from '../schema/case-review-schema'

/**
 * Get Citator Citing Cases
 *
 * @param parent_id - The parent ID of the case
 * @returns The citator citing cases response
 */
export async function getCitatorCitingCases(parent_id: string): Promise<CitatorCitingCasesResponse> {
  const sentry_transaction_id = nanoid()
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw Error(kErrorCodeAuthError)

  try {
    const apiUrl = kPaxtonAppApiRESTBaseUrl() + '/' + kCitatorGetCitingCasesApiPathV2 + '/' + parent_id

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
    }

    const response = await fetch(apiUrl, requestOptions)
    const data = await response.json()

    const validatedData = CitatorCitingCasesResponseSchema.parse(data)

    if (!response.ok) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })

        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`), {
          extra: {
            requestUrl: apiUrl,
            requestOptions: requestOptions,
            response: JSON.stringify(response),
            data: JSON.stringify(data),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiError(response.status, `Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`)
    }

    return validatedData
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })

        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
        })
      })
    }
    throw error
  }
}
